import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface InviteDialogProps {
  open: boolean;
  onClose: () => void;
  inviteEmail: string;
  setInviteEmail: (email: string) => void;
  inviteRole: string;
  setInviteRole: (role: string) => void;
  onInvite: () => void;
  canManageManagers: boolean;
}

const InviteDialog: React.FC<InviteDialogProps> = ({
  open,
  onClose,
  inviteEmail,
  setInviteEmail,
  inviteRole,
  setInviteRole,
  onInvite,
  canManageManagers,
}) => {
  const { t } = useTranslation();
  const [emailError, setEmailError] = useState<string>("");

  // Zodを使ったメールアドレスバリデーション
  const emailSchema = z.string().email();

  // カンマ区切りメールアドレスのバリデーション
  const validateEmails = (emailInput: string): { isValid: boolean; errorMessage: string } => {
    // 入力が空の場合
    if (!emailInput.trim()) {
      return { isValid: false, errorMessage: t("preferences.enterprise.error.emailRequired") };
    }

    // カンマ区切りのメールアドレスを配列に変換し、スペースを除去
    const emails = emailInput
      .split(",")
      .map((email) => email.replace(/[\s\u3000]/g, "")) // 半角・全角スペース除去
      .filter((email) => email.length > 0);

    // メールアドレスが1つもない場合
    if (emails.length === 0) {
      return { isValid: false, errorMessage: t("preferences.enterprise.error.emailRequired") };
    }

    // 各メールアドレスをZodでバリデーション
    for (const email of emails) {
      const result = emailSchema.safeParse(email);
      if (!result.success) {
        return {
          isValid: false,
          errorMessage: t("preferences.enterprise.error.emailValidationError", { email }),
        };
      }
    }

    return { isValid: true, errorMessage: "" };
  };

  // メールアドレス入力変更時のハンドラ
  const handleEmailChange = (value: string) => {
    setInviteEmail(value);

    if (value.trim()) {
      const validation = validateEmails(value);
      setEmailError(validation.errorMessage);
    } else {
      setEmailError("");
    }
  };

  // 招待実行時のバリデーション
  const handleInvite = () => {
    const validation = validateEmails(inviteEmail);
    if (!validation.isValid) {
      setEmailError(validation.errorMessage);
      return;
    }

    setEmailError("");
    onInvite();
  };

  // ダイアログが閉じられたときエラーをクリア
  useEffect(() => {
    if (!open) {
      setEmailError("");
    }
  }, [open]);

  const getRoleTranslation = (role: string) => {
    if (role === "owner") return t("preferences.enterprise.role.owner");
    if (role === "manager") return t("preferences.enterprise.role.manager");
    return t("preferences.enterprise.role.member");
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t("preferences.enterprise.inviteMember")}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t("preferences.enterprise.inviteDescription")}</DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          label={t("preferences.enterprise.invitation.email")}
          placeholder={t("preferences.enterprise.invitation.emailPlaceholder")}
          type="email"
          fullWidth
          variant="outlined"
          value={inviteEmail}
          onChange={(e) => handleEmailChange(e.target.value)}
          error={!!emailError}
          helperText={emailError}
          sx={{ mb: 2, mt: 2 }}
        />
        <FormControl fullWidth>
          <InputLabel id="role-select-label">{t("preferences.enterprise.invitation.role")}</InputLabel>
          <Select
            labelId="role-select-label"
            value={inviteRole}
            label={t("preferences.enterprise.invitation.role")}
            onChange={(e) => setInviteRole(e.target.value)}
          >
            <MenuItem value="member">{getRoleTranslation("member")}</MenuItem>
            <MenuItem value="manager" disabled={!canManageManagers}>
              {getRoleTranslation("manager")}
            </MenuItem>
            <MenuItem value="owner" disabled={!canManageManagers}>
              {getRoleTranslation("owner")}
            </MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button onClick={handleInvite} color="primary">
          {t("preferences.enterprise.invitation.invite")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InviteDialog;
